import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, IconButton, Stack } from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import GenericTable from "../Ui_elements/GenericTable";
import Btn from "../Ui_elements/Btn";
import RangeModal from "../HolidayPlanner/RangeModal";
import {
  createHolidayAbsence,
  deleteHolidayAbsence,
  fetchUserAbsences,
  updateHolidayAbsence,
} from "../../redux/Thunk/holidayCalendarModule/HolidayplannerThunk";
import { getStatusConfig, formatCellTimeRange } from "../HolidayPlanner/statusConfig";

const AbsenceManager = ({
  userId,
  userName,
  readOnly = false,
  showAddButton = true,
  pageTitle = "Absent List",
}) => {
  const dispatch = useDispatch();
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rangeModalOpen, setRangeModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const resourceOption = useMemo(
    () => [{ iUserId: userId, vUserName: userName || `User ${userId}` }],
    [userId, userName]
  );

  const loadAbsences = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await dispatch(fetchUserAbsences(userId)).unwrap();
      setAbsences(Array.isArray(data) ? data : []);
    } catch {
      setAbsences([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, userId]);

  useEffect(() => {
    loadAbsences();
  }, [loadAbsences]);

  const handleRangeSave = async payload => {
    const requestPayload = { ...payload, user_id: userId, refreshMatrix: false };
    try {
      if (editingRecord?.absentId) {
        await dispatch(
          updateHolidayAbsence({ id: editingRecord.absentId, payload: requestPayload })
        ).unwrap();
      } else {
        await dispatch(createHolidayAbsence(requestPayload)).unwrap();
      }
      setRangeModalOpen(false);
      setEditingRecord(null);
      await loadAbsences();
    } catch {
      // handled in thunk
    }
  };

  const handleRangeDelete = async () => {
    if (!editingRecord?.absentId) return;
    try {
      await dispatch(deleteHolidayAbsence({ id: editingRecord.absentId, refreshMatrix: false })).unwrap();
      setRangeModalOpen(false);
      setEditingRecord(null);
      await loadAbsences();
    } catch {
      // handled in thunk
    }
  };

  const handleDelete = async absentId => {
    try {
      await dispatch(deleteHolidayAbsence({ id: absentId, refreshMatrix: false })).unwrap();
      await loadAbsences();
    } catch {
      // handled in thunk
    }
  };

  const columns = [
    { name: "rowNumber", label: "No." },
    {
      name: "date_from",
      label: "Date From",
      options: {
        customBodyRender: value => (value ? dayjs(value).format("DD.MM.YYYY") : ""),
      },
    },
    {
      name: "date_to",
      label: "Date To",
      options: {
        customBodyRender: value => (value ? dayjs(value).format("DD.MM.YYYY") : ""),
      },
    },
    {
      name: "eStatus",
      label: "Status",
      options: {
        customBodyRender: value => getStatusConfig(value).label,
      },
    },
    {
      name: "hours",
      label: "Hours",
      options: {
        customBodyRender: value => value || "—",
      },
    },
    { name: "vNotes", label: "Notes" },
  ];

  if (!readOnly) {
    columns.push({
      name: "actions",
      label: "Actions",
      options: {
        customBodyRenderLite: dataIndex => {
          const item = absences[dataIndex];
          const isPast = dayjs().isAfter(dayjs(item.date_to), "day");

          return (
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                disabled={isPast}
                onClick={() => {
                  setEditingRecord({
                    absentId: item.absentId,
                    user_id: item.user_id,
                    date_from: item.date_from,
                    date_to: item.date_to,
                    eStatus: item.eStatus,
                    time_from: item.time_from,
                    time_to: item.time_to,
                    vNotes: item.vNotes,
                  });
                  setRangeModalOpen(true);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                disabled={isPast}
                onClick={() => handleDelete(item.absentId)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Stack>
          );
        },
      },
    });
  }

  const tableData = absences.map((item, index) => ({
    rowNumber: index + 1,
    ...item,
    hours: formatCellTimeRange(item.time_from, item.time_to),
  }));

  return (
    <Box>
      {!readOnly && showAddButton && (
        <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
          <Btn
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingRecord(null);
              setRangeModalOpen(true);
            }}
          >
            Add Absence
          </Btn>
        </Grid>
      )}

      <GenericTable
        columns={columns}
        data={tableData}
        showAction={false}
        showDelete={false}
        displayColumns={columns.length}
        displayRows={10}
        showEdit={false}
        pageTitle={pageTitle}
        loading={loading}
      />

      {!readOnly && (
        <RangeModal
          open={rangeModalOpen}
          onClose={() => {
            setRangeModalOpen(false);
            setEditingRecord(null);
          }}
          isFreelancer
          loginUserId={userId}
          resource={resourceOption}
          editingRecord={editingRecord}
          onSave={handleRangeSave}
          onDelete={handleRangeDelete}
        />
      )}
    </Box>
  );
};

export default AbsenceManager;
